import { BaseError, type BaseErrorOptions } from '@canvas/error';
import { sleep } from '@canvas/sleep';

export type UnrecoverableNetworkErrorType =
  | 'timeout'
  | 'bad-request'
  | 'unknown'
  | 'not-found';

/**
 * Unrecoverable network error that we do not attempt to retry
 */
export class UnrecoverableNetworkError extends BaseError {
  constructor(
    message: string,
    readonly errorType: UnrecoverableNetworkErrorType = 'unknown',
    options?: BaseErrorOptions
  ) {
    super({ name: 'UnrecoverableNetworkError', ...options }, message);
  }
}

/**
 * Recoverable network error that we may attempt to retry
 */
export class RecoverableNetworkError extends BaseError {
  constructor(message: string, options?: BaseErrorOptions) {
    super({ name: 'RecoverableNetworkError', ...options }, message);
  }
}

/**
 * Check if an error is a recoverable network error
 * @param error The error to check
 * @returns whether the error is a recoverable network error
 */
export const isRecoverableNetworkError = (error: any) =>
  error instanceof RecoverableNetworkError;

export const DEFAULT_RETRY_TIME_MS = 500;
export const DEFAULT_RETRY_BACKOFF = 1.5;
export const DEFAULT_RETRY_MAX_MS = 60_000;

/**
 * Compute how long to wait for subsequent retries
 *
 * Uses both backoff and jitter to smooth reconnect load
 *
 * Once `attempt` gets sufficiently high (i.e. infinity retry), the timeout will
 * get sampled within the window of size `retryMaxMs`.
 *
 * @param attempt The attempt number, starting at 1
 * @param retryTimeMs The default time between retries
 * @param retryBackoff The factor by which to increase retries
 * @param retryMaxMs The maximum retry time
 */
export function getRetryTimeout(
  attempt: number,
  retryTimeMs = DEFAULT_RETRY_TIME_MS,
  retryBackoff = DEFAULT_RETRY_BACKOFF,
  retryMaxMs = DEFAULT_RETRY_MAX_MS
) {
  function getBackoff(attempt: number) {
    return Math.min(retryTimeMs * Math.pow(retryBackoff, attempt - 1), retryMaxMs);
  }

  const backoff = getBackoff(attempt);
  const idealUpper = backoff * 2;
  const upperBound = Math.min(idealUpper, retryMaxMs);

  // If the upper bound is equal to the ideal upper bound, we can simply use what we got
  // back from `getBackoff`, otherwise we need to shift the window down so it's within the max
  const lowerBound =
    upperBound === idealUpper ? backoff : Math.max(upperBound - backoff, 0);
  const jitterFactor = Math.random();

  // Finally, sample a random point within the window
  const nextBackoff = lowerBound + (upperBound - lowerBound) * jitterFactor;

  return nextBackoff;
}

type RetryResult<T> =
  | {
      success: true;
      result: T;
    }
  | {
      success: false;
      error: Error | unknown;
    };

interface RetryOptions<ResponseShape, RequestShape = string | URL | Request> {
  callback: (
    attempt: number,
    enhanceWithAttempt: (request: RequestShape) => RequestShape
  ) => Promise<ResponseShape>;
  maxRetries: number;
  retryTimeMs?: number;
  retryBackoff?: number;
  retryMaxMs?: number;
  shouldRetry?: (error: Error) => Promise<boolean> | boolean;
  signal?: AbortSignal;
  /**
   * By default, when running under jest (`process.env.MODE === 'test'`) we
   * skip retries entirely so that intentionally-failing network mocks do not
   * cause tests to wait through several backoff sleeps. Some callers rely on
   * the retry behaviour itself being executed under test (e.g. so they can
   * assert the number of attempts made). Set this to `true` to opt out of the
   * test-mode short-circuit for this individual call.
   */
  retryInTestMode?: boolean;
}

/**
 * Enhance the current request with a `retryAttempt` query parameter in the
 * request URL, so that response handlers (e.g. upstream servers) can be made
 * aware that they are being requested for a retry, and act accordingly - e.g.,
 * tuning the metrics which they send (normal request vs retry request).
 *
 * @param attempt current retry attempt
 * @param requestOrUrl current request object
 *
 * @returns a request object which can be utilised in a fetch retry request,
 * with the `?retryAttempt=<n>` query parameter added. If the attempt is the
 * first one, or we experience an error adding this query parameter, we fallback
 * to the existing request object, rather than creating a new one for the retry.
 */
export const enhanceRequestWithAttempt = (
  attempt: number,
  requestOrUrl: string | URL | Request
) => {
  try {
    const retryAttempt = attempt - 1;
    if (retryAttempt >= 1) {
      if (typeof requestOrUrl === 'string' || requestOrUrl instanceof URL) {
        const url = new URL(requestOrUrl);
        url.searchParams.set('retryAttempt', retryAttempt.toString());

        return url.href;
      } else {
        const url = new URL(requestOrUrl.url);
        url.searchParams.set('retryAttempt', retryAttempt.toString());

        const request = new Request(url, requestOrUrl);
        return request;
      }
    }

    return requestOrUrl;
  } catch (_err) {
    return requestOrUrl;
  }
};

/**
 * A utility function to retry an async action, such as a request, with a backoff that uses both backoff and jitter.
 *
 * @param params.callback - the callback to retry
 * @param params.shouldRetry - return a boolean indicating if a retry should be attempted
 * @param params.retryTimeMs - time to retry, defaults to 500ms
 * @param params.retryBackoff - The factor by which to increase retries, defaults to 1.5
 * @param params.retryMaxMs - the maximum retry time, defaults to 60s
 * @param params.maxRetries - the maximum number of retries. Use Number.POSITIVE_INFINITY to retry infinitely
 * @param params.signal - an abort signal to abort the retry
 * @param params.retryInTestMode - opt out of the global `MODE === 'test'`
 *   short-circuit so retry behaviour runs (and is testable) under jest.
 * @returns - a promise containing the result of the successful retry
 */
export async function retryWithBackoff<T>({
  shouldRetry = () => true,
  retryTimeMs = DEFAULT_RETRY_TIME_MS,
  retryBackoff = DEFAULT_RETRY_BACKOFF,
  retryMaxMs = DEFAULT_RETRY_MAX_MS,
  maxRetries,
  callback,
  signal,
  retryInTestMode = false,
}: RetryOptions<T>): Promise<T> {
  async function runAttempt(attempt: number): Promise<RetryResult<T>> {
    try {
      const result = await callback(attempt, (request) =>
        enhanceRequestWithAttempt(attempt, request)
      );
      return {
        success: true,
        result,
      };
    } catch (e) {
      const avoidSlowNetworkFailureNetworkMocks =
        !retryInTestMode && globalThis.process?.env?.MODE === 'test';
      if (avoidSlowNetworkFailureNetworkMocks) {
        throw e;
      }

      if (attempt >= maxRetries) {
        // If we've reached the maximum number of retries, don't sleep again
        return {
          success: false,
          error: e,
        };
      }

      if (e instanceof Error && (await shouldRetry(e))) {
        const timeout = getRetryTimeout(attempt, retryTimeMs, retryBackoff, retryMaxMs);
        await sleep(timeout);

        return {
          success: false,
          error: e,
        };
      } else {
        throw e;
      }
    }
  }

  // This CANNOT use recursion, as it will blow the stack with a large number of retries
  let lastError: Error | unknown | undefined = undefined;
  let attempt = 0;
  do {
    if (signal?.aborted) {
      if (lastError) {
        throw lastError;
      } else {
        signal.throwIfAborted();
      }
    }

    const result = await runAttempt(attempt + 1);
    if (result.success) {
      return result.result;
    } else {
      lastError = result.error;
    }

    attempt++;
  } while (attempt < maxRetries);

  throw lastError;
}

export interface WaitForRetryOptions {
  attempt: number;
  retryTimeMs?: number;
  retryBackoff?: number;
  retryMaxMs?: number;
}

export async function waitForRetry({
  attempt,
  retryTimeMs = DEFAULT_RETRY_TIME_MS,
  retryBackoff = DEFAULT_RETRY_BACKOFF,
  retryMaxMs = DEFAULT_RETRY_MAX_MS,
}: WaitForRetryOptions): Promise<void> {
  if (attempt === 0) {
    return;
  }

  await sleep(getRetryTimeout(attempt, retryTimeMs, retryBackoff, retryMaxMs));
}
